import { useEffect, useState, type FormEvent } from "react";
import { X, Download, FileText, CheckCircle, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { toast } from "sonner";
import { trackEvent, trackFormSubmission } from "@/lib/analytics";

const STORAGE_KEY = "finendra_exit_intent_dismissed";

export function ExitIntentModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [industry, setIndustry] = useState("");
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Check if dismissed in the last 14 days
    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const parsed = parseInt(dismissedAt, 10);
      if (Date.now() - parsed < 14 * 24 * 60 * 60 * 1000) {
        return;
      }
    }

    let hasTriggered = false;

    // Apple Design §16 Agency: Only trigger on true deliberate desktop exit towards tab bar
    const handleMouseLeave = (e: MouseEvent) => {
      if (hasTriggered) return;
      if (e.clientY <= 8 && e.movementY < 0) {
        hasTriggered = true;
        setIsOpen(true);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        localStorage.setItem(STORAGE_KEY, Date.now().toString());
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const close = () => {
    setIsOpen(false);
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      toast.error("Please enter a valid work email address.");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, industry, website }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to submit. Please try again.");
      }

      setSuccess(true);
      toast.success("Checklist ready! Your download has started.");

      trackFormSubmission("exit_intent_checklist", {
        industry: industry || "Not specified",
      });
      trackEvent("pdf_download", {
        pdfName: "monthly-close-checklist.pdf",
        source: "exit_intent",
      });

      const link = document.createElement("a");
      link.href = "/assets/monthly-close-checklist.pdf";
      link.download = "Finendra-Monthly-Close-Checklist-Founders.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        close();
      }, 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Something went wrong.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby="exit-modal-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) close();
          }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/55 backdrop-blur-md backdrop-saturate-150 p-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ type: "spring", bounce: 0, duration: 0.3 }}
            className="relative w-full max-w-lg rounded-3xl border border-brand-navy/10 bg-background p-6 md:p-8 shadow-2xl"
          >
            <button
              onClick={close}
              type="button"
              aria-label="Close dialog"
              className="absolute right-5 top-5 rounded-full p-1.5 text-brand-navy/60 hover:bg-brand-surface hover:text-brand-navy active:scale-90 transition-transform cursor-pointer"
            >
              <X className="size-5" />
            </button>

            {success ? (
              <div className="py-8 text-center">
                <CheckCircle className="mx-auto size-14 text-brand-accent animate-in zoom-in" />
                <h3
                  id="exit-modal-title"
                  className="mt-4 font-display text-2xl font-bold text-brand-navy tracking-tight"
                >
                  Download Started!
                </h3>
                <p className="mt-2 text-sm text-brand-navy/70">
                  The Monthly Close Checklist for Founders is now in your downloads.
                </p>
              </div>
            ) : (
              <div>
                <div className="inline-flex items-center gap-1.5 rounded-full bg-brand-accent/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-brand-navy">
                  <FileText className="size-3 text-brand-accent" />
                  Before you leave
                </div>

                <h3
                  id="exit-modal-title"
                  className="mt-3 font-display text-2xl font-extrabold text-brand-navy tracking-tight"
                >
                  Get the Free 27-Point Monthly Close Checklist
                </h3>

                <p className="mt-2 text-sm text-brand-navy/70 leading-relaxed">
                  Don't leave month-end close to guesswork. Download the exact checklist used by
                  VC-backed startups and fractional CFOs to close books by the 5th.
                </p>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    className="sr-only"
                    aria-hidden="true"
                  />

                  <div>
                    <label
                      htmlFor="exit-email"
                      className="block text-xs font-semibold text-brand-navy/80 mb-1.5"
                    >
                      Work Email Address
                    </label>
                    <input
                      id="exit-email"
                      type="email"
                      required
                      placeholder="founder@startup.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-xl border border-brand-navy/20 bg-background px-4 py-3 text-sm text-brand-navy focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="exit-industry"
                      className="block text-xs font-semibold text-brand-navy/80 mb-1.5"
                    >
                      Company Type
                    </label>
                    <select
                      id="exit-industry"
                      value={industry}
                      onChange={(e) => setIndustry(e.target.value)}
                      className="w-full rounded-xl border border-brand-navy/20 bg-background px-4 py-3 text-sm text-brand-navy focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
                    >
                      <option value="">Select your industry</option>
                      <option value="SaaS & Technology">SaaS & Technology</option>
                      <option value="E-commerce & DTC">E-commerce & DTC</option>
                      <option value="Pre-seed / Seed Startup">Pre-seed / Seed Startup</option>
                      <option value="Nonprofit & Research">Nonprofit & Research</option>
                      <option value="Agency / Consultancy">Agency / Consultancy</option>
                      <option value="India-US Cross-Border">India-US Cross-Border</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-navy py-3.5 text-sm font-bold text-background transition-colors hover:bg-brand-accent active:scale-[0.98] disabled:opacity-60 cursor-pointer"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="size-4 animate-spin" /> Preparing PDF...
                      </>
                    ) : (
                      <>
                        <Download className="size-4" /> Download Checklist PDF
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={close}
                    className="w-full text-center text-xs text-brand-navy/50 hover:text-brand-navy transition-colors py-1 cursor-pointer"
                  >
                    No thanks, I'll close my books manually
                  </button>
                </form>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
